import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, Loader2, RefreshCw, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import SecurityFindingsList from '../../components/Security/SecurityFindingsList'
import { securityApi, severityMeta } from '../../services/securityApi'
import { getTenantId } from '../../config/tenant'
import { formatDateTime } from '../../utils/format'

export default function SecurityPage() {
  const { t } = useTranslation()
  const tenantId = getTenantId()

  const overview = useQuery({
    queryKey: ['securityOverview', tenantId],
    queryFn: () => securityApi.overview(tenantId),
    enabled: !!tenantId,
    refetchInterval: 120000,
  })
  const data = overview.data

  const cards = [
    ['critical', data?.critical],
    ['high', data?.high],
    ['medium', data?.medium],
    ['low', data?.low],
  ]

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-theme-secondary" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{t('security.title', 'Segurança')}</h1>
          <p className="text-sm text-gray-500">
            {t('security.subtitle', 'Varredura diária (somente leitura) dos dispositivos: usuários, persistência, exposição na internet, firewall e logs.')}
          </p>
        </div>
        <button onClick={() => overview.refetch()} className="btn btn-secondary text-sm" title="Atualizar">
          <RefreshCw className={clsx('w-4 h-4', overview.isFetching && 'animate-spin')} />
        </button>
      </div>

      {overview.isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map(([sev, value]) => {
              const meta = severityMeta(sev)
              return (
                <div key={sev} className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className={clsx('w-2 h-2 rounded-full', meta.dot)} /> {meta.label}
                  </div>
                  <div className="text-2xl font-semibold text-gray-900 mt-1">{value ?? 0}</div>
                </div>
              )
            })}
          </div>

          <div className="text-xs text-gray-500">
            {data?.lastScanAt
              ? <>Última varredura: {formatDateTime(data.lastScanAt)} · {data.devicesScanned} dispositivo(s)</>
              : 'Nenhuma varredura ainda — a primeira roda automaticamente em até 24h.'}
            {!!data?.devicesUnreachable && (
              <span className="ml-2 text-red-700">
                <AlertTriangle className="w-3 h-3 inline mr-0.5" />
                {data.devicesUnreachable} sem acesso de gestão
              </span>
            )}
          </div>

          {/* Situação por dispositivo */}
          {!!data?.devices?.length && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Dispositivo</th>
                    <th className="text-left font-medium px-3 py-2">IPs públicos</th>
                    <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Varredura</th>
                    <th className="text-right font-medium px-3 py-2">Crít.</th>
                    <th className="text-right font-medium px-3 py-2">Alto</th>
                    <th className="text-right font-medium px-3 py-2">Médio</th>
                    <th className="text-right font-medium px-3 py-2">Baixo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.devices.map((d) => (
                    <tr key={d.managedDeviceId} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <Link to={`/managed-devices/${d.managedDeviceId}`} className="font-medium text-gray-900 hover:text-theme-primary">
                          {d.deviceName}
                        </Link>
                        <div className="text-gray-400">{d.kind}</div>
                        {d.reachable === false && (
                          <div className="text-red-700" title={d.error || ''}>sem acesso de gestão</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-600">{d.publicIps || '—'}</td>
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{d.scannedAt ? formatDateTime(d.scannedAt) : '—'}</td>
                      <td className={clsx('px-3 py-2 text-right', d.critical > 0 && 'text-red-700 font-semibold')}>{d.critical}</td>
                      <td className={clsx('px-3 py-2 text-right', d.high > 0 && 'text-red-600')}>{d.high}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{d.medium}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{d.low}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="text-sm font-semibold text-gray-900 pt-2">Achados</h2>
          <SecurityFindingsList tenantId={tenantId} showDevice />
        </>
      )}
    </div>
  )
}
